// Replace the PDP's "Select Variant" popup (item_configure.js's dialog) with
// the same Amazon-style inline swatches used on listing cards
// (product_variant_swatches.js), so shoppers pick a size/color right on the
// product page. Wires into the cart the same way the non-variant PDP flow
// already does: a dynamically created .item-cart[data-variant-item-code]
// block, which pdp_quantity.js (now re-target-aware) drives the qty spinner
// for.
//
// Standardized against product_variant_swatches.js (the listing-card
// implementation) so a shopper gets the same variant-picking behavior on
// both surfaces: colour swatches, a default preselection so the page never
// sits idle, cart-preselection sync, and the same stock-corrected
// resolve/fallback-search flow. Structural differences from the card are
// intentional, not gaps: one swatch set instead of many, and a fuller
// price/stock/cart-actions block (.item-cart) instead of a compact button,
// since a PDP has room for both.
import { resolveSwatchColor, getSwatchColorOverrides } from './swatch_colors_bridge.js';

frappe.ready(function () {
	if (!window.webshop) return;

	var page = document.querySelector('[data-doctype="Website Item"][source-type="Generator"]');
	if (!page) return;

	var configureBtn = document.querySelector('.btn-configure');
	if (!configureBtn) return; // not a variant template page

	var templateItemCode = configureBtn.getAttribute('data-item-code');
	if (!templateItemCode) return;

	// Disable the old popup trigger rather than removing it, in case other
	// script on the page references it - just take it out of the flow.
	configureBtn.style.display = 'none';
	configureBtn.disabled = true;

	var swatchWrapper = document.createElement('div');
	swatchWrapper.className = 'pdp-variant-swatches-wrapper mb-4';
	swatchWrapper.innerHTML =
		'<div class="variant-swatches loading" data-template-item-code="' + frappe.utils.escape_html(templateItemCode) + '">' +
			'<span class="variant-swatches-loading text-muted">' + __("Loading options...") + '</span>' +
		'</div>';
	configureBtn.parentNode.insertBefore(swatchWrapper, configureBtn);

	var swatchContainer = swatchWrapper.querySelector('.variant-swatches');
	var itemCartEl = null; // created on first resolved match, then re-targeted on switches
	var latestRequestId = 0;

	// Same fix as product_variant_swatches.js: some templates have duplicate
	// "Item Variant Attribute" rows from past data imports, which makes the
	// API return the same attribute twice.
	function dedupeAttributes(attributes) {
		var seen = {};
		var result = [];
		(attributes || []).forEach(function (attr) {
			if (seen[attr.attribute]) return;
			seen[attr.attribute] = true;
			result.push(attr);
		});
		return result;
	}

	// --- Colour swatches: same contract as product_variant_swatches.js so a
	// colour reads identically on the card and here - a coloured dot instead
	// of a text button. Unlike the card, there's no product-image container
	// for this script to overlay onto here, so Colour renders as a normal
	// row (just first, and without a text label - the dot already says
	// which colour it is).

	function isColourAttribute(name) {
		return /^colou?r$/i.test(String(name || '').trim());
	}

	function renderColourRowHtml(attr, swatchColorOverrides) {
		var html = '<div class="variant-attr-row" data-attribute="' + frappe.utils.escape_html(attr.attribute) + '">';
		html += '<div class="variant-pill-group">';
		attr.values.forEach(function (value) {
			var safeValue = frappe.utils.escape_html(value);
			var swatchColor = resolveSwatchColor(value, swatchColorOverrides) || '#c4c4c4';
			html += '<button type="button" class="variant-pill variant-pill--color"' +
				' data-attribute="' + frappe.utils.escape_html(attr.attribute) + '"' +
				' data-value="' + safeValue + '"' +
				' title="' + safeValue + '" aria-label="' + safeValue + '"' +
				' style="--swatch-color: ' + swatchColor + '">' +
				'<span class="sr-only">' + safeValue + '</span>' +
			'</button>';
		});
		html += '</div></div>';
		return html;
	}

	function renderAttrRowHtml(attr) {
		var html = '<div class="variant-attr-row" data-attribute="' + frappe.utils.escape_html(attr.attribute) + '">';
		html += '<span class="variant-attr-label">' + frappe.utils.escape_html(attr.attribute) + '</span>';
		html += '<div class="variant-pill-group">';
		attr.values.forEach(function (value) {
			var safeValue = frappe.utils.escape_html(value);
			html += '<button type="button" class="variant-pill"' +
				' data-attribute="' + frappe.utils.escape_html(attr.attribute) + '"' +
				' data-value="' + safeValue + '">' +
				safeValue +
			'</button>';
		});
		html += '</div></div>';
		return html;
	}

	function renderAttributeRowsHtml(attributes, swatchColorOverrides) {
		var colourAttr = null;
		var otherAttrs = [];
		attributes.forEach(function (attr) {
			if (!attr.values || !attr.values.length) return;
			if (!colourAttr && isColourAttribute(attr.attribute)) {
				colourAttr = attr;
			} else {
				otherAttrs.push(attr);
			}
		});

		var html = colourAttr ? renderColourRowHtml(colourAttr, swatchColorOverrides) : '';
		otherAttrs.forEach(function (attr) { html += renderAttrRowHtml(attr); });
		return html;
	}

	// getSwatchColorOverrides() (swatch_colors_bridge.js -> websitehq's own
	// cached fetch) is shared with product_variant_swatches.js if both
	// happen to run on the same page (a template PDP can show a "similar
	// items" carousel of cards below it) -- only the attributes call below
	// is genuinely specific to this one PDP.
	function loadVariantOptions() {
		var attributesPromise = new Promise(function (resolve) {
			frappe.call({
				method: 'webshop.webshop.variant_selector.utils.get_attributes_and_values',
				args: { item_code: templateItemCode },
				callback: function (r) { resolve((r && r.message) || []); },
				error: function () { resolve([]); }
			});
		});

		Promise.all([attributesPromise, getSwatchColorOverrides()]).then(function (results) {
			var attributes = dedupeAttributes(results[0]);
			var swatchColorOverrides = results[1];
			swatchContainer.classList.remove('loading');

			if (!attributes.length) {
				swatchContainer.innerHTML = '';
				return;
			}

			swatchContainer.innerHTML = renderAttributeRowsHtml(attributes, swatchColorOverrides);
			applyDefaultPreselection();
			applyCartPreselection();
		});
	}

	// --- Selection helpers, scoped to this page's single swatchContainer
	// (the card equivalents are scoped per-card since a listing has many).

	function getSelectionSource() {
		return swatchContainer.dataset.selectionSource || '';
	}

	function clearActivePills() {
		Array.prototype.forEach.call(swatchContainer.querySelectorAll('.variant-pill.active'), function (pill) {
			pill.classList.remove('active');
		});
	}

	function clearOosPills() {
		Array.prototype.forEach.call(swatchContainer.querySelectorAll('.variant-pill-oos'), function (pill) {
			pill.classList.remove('variant-pill-oos');
		});
	}

	function getActiveSelection() {
		var selection = {};
		Array.prototype.forEach.call(swatchContainer.querySelectorAll('.variant-pill.active'), function (pill) {
			selection[pill.getAttribute('data-attribute')] = pill.getAttribute('data-value');
		});
		return selection;
	}

	function applySelection(selection) {
		Array.prototype.forEach.call(swatchContainer.querySelectorAll('.variant-pill'), function (pill) {
			var attribute = pill.getAttribute('data-attribute');
			var value = pill.getAttribute('data-value');
			pill.classList.toggle('active', selection[attribute] === value);
		});
	}

	// --- Cart preselection sync: pre-highlight whichever variant of this
	// template is already in the shopper's cart, kept live on cart-updated.
	// Selections can come from three sources, in increasing priority:
	// 'default' (first value per row, so the page never sits idle), 'cart'
	// (matches the shopper's actual cart), and 'user' (an explicit click). A
	// lower-priority source may be overwritten by a higher one, never the
	// reverse - e.g. a cart match arriving after a default pick should still
	// correct it, but must never clobber a choice the shopper made by hand.

	var cartSelection = null; // {item_code, attributes} | null, for templateItemCode only
	var cartSelectionRefreshTimer = null;

	// Debounced like product_variant_swatches.js's own refresh: resolving a
	// pre-selected variant triggers 'cart-updated' (to sync the qty
	// spinner), which would otherwise re-trigger this same refresh in a
	// loop. Collapsing bursts into one call after a short quiet period keeps
	// it self-stabilizing instead of chaining network round-trips.
	function refreshCartSelection() {
		if (cartSelectionRefreshTimer) return;
		cartSelectionRefreshTimer = setTimeout(function () {
			cartSelectionRefreshTimer = null;
			doRefreshCartSelection();
		}, 200);
	}

	function doRefreshCartSelection() {
		frappe.call({
			method: 'raftor_webhq.checkout.simple_checkout.get_cart_items_snapshot',
			callback: function (r) {
				var items = (r && r.message && r.message.items) || [];
				var itemCodes = items.map(function (row) { return row.item_code; }).filter(Boolean);

				if (!itemCodes.length) {
					cartSelection = null;
					return;
				}

				frappe.call({
					method: 'raftor_webhq.catalog.api.get_variant_selections_for_items',
					args: { item_codes: itemCodes },
					callback: function (r2) {
						var selections = (r2 && r2.message) || {};
						cartSelection = selections[templateItemCode] || null;
						applyCartPreselection();
					}
				});
			}
		});
	}

	function applyCartPreselection() {
		if (!cartSelection || !cartSelection.attributes) return;
		// Don't clobber a choice the shopper has already made on this visit,
		// but a same-or-lower priority 'default' auto-pick is fair game.
		if (getSelectionSource() === 'user') return;

		var attributes = cartSelection.attributes;
		clearActivePills();
		clearOosPills();
		delete swatchContainer._stockSearch;
		var appliedAny = false;
		Array.prototype.forEach.call(swatchContainer.querySelectorAll('.variant-pill'), function (pill) {
			var attribute = pill.getAttribute('data-attribute');
			var value = pill.getAttribute('data-value');
			if (Object.prototype.hasOwnProperty.call(attributes, attribute) && attributes[attribute] === value) {
				pill.classList.add('active');
				appliedAny = true;
			}
		});

		if (appliedAny) {
			swatchContainer.dataset.selectionSource = 'cart';
			// skipResync: we already know this exact item is in the cart (that's
			// why we're pre-selecting it) - no need to re-trigger cart-updated,
			// which would just re-fetch this same selection data in a loop.
			resolveVariant(/* skipResync */ true);
		}
	}

	// Auto-pick the first value of every attribute row (e.g. the first
	// size) so the page always lands with a price and an active cart action
	// instead of an idle, all-unselected picker. Only applies when nothing
	// else has claimed a selection yet - a cart match or a user click both
	// take priority and are applied separately.
	function applyDefaultPreselection() {
		if (getSelectionSource()) return;
		if (swatchContainer.querySelector('.variant-pill.active')) return;

		var appliedAny = false;
		Array.prototype.forEach.call(swatchContainer.querySelectorAll('.variant-attr-row'), function (row) {
			var firstPill = row.querySelector('.variant-pill');
			if (firstPill) {
				firstPill.classList.add('active');
				appliedAny = true;
			}
		});

		if (appliedAny) {
			swatchContainer.dataset.selectionSource = 'default';
			resolveVariant();
		}
	}

	// --- Stock-corrected resolve, with fallback search across other
	// attribute combinations when the resolved exact match isn't actually
	// buyable. Ported from product_variant_swatches.js: the underlying
	// get_next_attribute_and_values' own available_qty can't be trusted -
	// it looks up the resolved variant's own Website Item.website_warehouse,
	// but this catalog only publishes the *template* as a Website Item (no
	// per-variant Website Item exists), so that lookup silently resolves to
	// no warehouse and always reports 0 stock. raftor_webhq.catalog.api.
	// get_product_info wraps the same core webshop call with a warehouse
	// fallback fix (see stock_guard.enrich_product_info) and returns the
	// identical {product_info, cart_settings} shape this file already
	// renders from - so it replaces both the old stock-gate gap and the
	// second, unfixed product-info call the PDP used to make.

	var MAX_STOCK_SEARCH_COMBINATIONS = 24;

	function selectionKey(selection) {
		return Object.keys(selection).sort().map(function (attribute) {
			return attribute + '=' + selection[attribute];
		}).join('&');
	}

	// Builds every attribute combination for this template, ordered to
	// explore outward from what's currently selected: the row that just
	// changed (changedAttribute) cycles through its own values first while
	// every other row stays at the shopper's actual pick; only once that's
	// exhausted does another row move away from the shopper's pick, one
	// step at a time.
	function buildStockSearchOrder(changedAttribute, currentSelection) {
		var rows = [];
		Array.prototype.forEach.call(swatchContainer.querySelectorAll('.variant-attr-row'), function (row) {
			var attribute = row.getAttribute('data-attribute');
			var values = Array.prototype.map.call(row.querySelectorAll('.variant-pill'), function (pill) {
				return pill.getAttribute('data-value');
			});
			var startIndex = Math.max(0, values.indexOf(currentSelection[attribute]));
			rows.push({
				attribute: attribute,
				values: values.slice(startIndex).concat(values.slice(0, startIndex))
			});
		});

		rows.sort(function (a, b) {
			if (a.attribute === changedAttribute) return -1;
			if (b.attribute === changedAttribute) return 1;
			return 0;
		});

		var combos = [{}];
		rows.forEach(function (rowInfo) {
			var next = [];
			rowInfo.values.forEach(function (value) {
				combos.forEach(function (combo) {
					var copy = {};
					for (var key in combo) copy[key] = combo[key];
					copy[rowInfo.attribute] = value;
					next.push(copy);
				});
			});
			combos = next;
		});

		return combos.slice(0, MAX_STOCK_SEARCH_COMBINATIONS);
	}

	function advanceStockSearch(skipResync, changedAttribute) {
		var current = getActiveSelection();

		if (!swatchContainer._stockSearch) {
			var rowAttr = changedAttribute;
			if (!rowAttr) {
				var firstRow = swatchContainer.querySelector('.variant-attr-row');
				rowAttr = firstRow && firstRow.getAttribute('data-attribute');
			}
			swatchContainer._stockSearch = {
				changedAttribute: rowAttr,
				baseline: current, // the shopper's actual picks when the hunt started
				combos: buildStockSearchOrder(rowAttr, current),
				index: 0,
				tried: {}
			};
		}
		var search = swatchContainer._stockSearch;
		search.tried[selectionKey(current)] = true;

		// A failure where every *other* attribute still matches the baseline
		// is an unambiguous "this exact value has no stock given the rest of
		// the selection" - safe to grey out permanently.
		var isFirstLapFailure = Object.keys(search.baseline).every(function (attribute) {
			return attribute === search.changedAttribute || current[attribute] === search.baseline[attribute];
		});
		if (isFirstLapFailure) {
			var changedRow = swatchContainer.querySelector('.variant-attr-row[data-attribute="' + search.changedAttribute + '"]');
			if (changedRow) {
				Array.prototype.forEach.call(changedRow.querySelectorAll('.variant-pill'), function (pill) {
					if (pill.getAttribute('data-value') === current[search.changedAttribute]) {
						pill.classList.remove('active');
						pill.classList.add('variant-pill-oos');
					}
				});
			}
		}

		for (; search.index < search.combos.length; search.index++) {
			var candidate = search.combos[search.index];
			var key = selectionKey(candidate);
			if (search.tried[key]) continue;
			search.tried[key] = true;
			search.index++;
			applySelection(candidate);
			resolveVariant(skipResync, search.changedAttribute);
			return;
		}

		delete swatchContainer._stockSearch;
		showOutOfStock();
	}

	function resolveVariant(skipResync, changedAttribute) {
		var selected = getActiveSelection();
		var requestId = ++latestRequestId;

		if (!Object.keys(selected).length) {
			Array.prototype.forEach.call(swatchContainer.querySelectorAll('.variant-pill'), function (pill) {
				pill.classList.remove('variant-pill-disabled');
			});
			hideItemCart();
			return;
		}

		frappe.call({
			method: 'webshop.webshop.variant_selector.utils.get_next_attribute_and_values',
			args: {
				item_code: templateItemCode,
				selected_attributes: selected
			},
			callback: function (r) {
				if (requestId !== latestRequestId) return;

				var data = r && r.message;
				if (!data) return;

				// Pills within an attribute row the shopper has already picked
				// stay switchable; only *other*, unselected attribute rows get
				// narrowed - the API is built for the PDP dialog's "Clear" flow,
				// which doesn't apply to inline swatches.
				var validOptions = data.valid_options_for_attributes || {};
				Array.prototype.forEach.call(swatchContainer.querySelectorAll('.variant-pill'), function (pill) {
					var attribute = pill.getAttribute('data-attribute');
					var value = pill.getAttribute('data-value');
					if (Object.prototype.hasOwnProperty.call(selected, attribute)) {
						pill.classList.remove('variant-pill-disabled');
						return;
					}
					var validValues = validOptions[attribute];
					var isValid = validValues ? validValues.indexOf(value) !== -1 : true;
					pill.classList.toggle('variant-pill-disabled', !isValid);
				});

				if (data.exact_match && data.exact_match.length === 1) {
					checkStockAndRender(data.exact_match[0], requestId, skipResync);
				} else if (swatchContainer._stockSearch) {
					// This combination doesn't exist as a real item either -
					// keep hunting rather than abandoning the search here.
					advanceStockSearch(skipResync, changedAttribute);
				} else {
					hideItemCart();
				}
			}
		});
	}

	// Swap the PDP's main photo to the resolved variant's own image, so
	// picking a colour (or any attribute that maps to a distinct photo)
	// updates what the shopper sees. get_product_info now returns this via
	// enrich_product_info (stock_guard.py) - core get_product_info_for_website
	// never included an image at all.
	function updateProductImage(imageUrl) {
		if (!imageUrl) return;
		var img = document.querySelector('.product-image img');
		if (!img || img.getAttribute('src') === imageUrl) return;
		img.setAttribute('src', imageUrl);
	}

	function checkStockAndRender(matchedItemCode, requestId, skipResync) {
		frappe.call({
			method: 'raftor_webhq.catalog.api.get_product_info',
			args: { item_code: matchedItemCode, skip_quotation_creation: 1 },
			callback: function (r) {
				if (requestId !== latestRequestId) return;

				var message = r && r.message;
				var productInfo = (message && message.product_info) || {};

				if (productInfo.can_add_to_cart === false) {
					advanceStockSearch(skipResync);
					return;
				}

				// A buyable match - any in-progress hunt for an in-stock
				// combination is done.
				delete swatchContainer._stockSearch;
				updateProductImage(productInfo.image);
				renderItemCart(matchedItemCode, productInfo, (message && message.cart_settings) || {}, skipResync, message && message.item_action);
			}
		});
	}

	function hideItemCart() {
		if (itemCartEl) {
			itemCartEl.classList.add('hide');
		}
	}

	function showOutOfStock() {
		if (!itemCartEl) {
			itemCartEl = document.createElement('div');
			itemCartEl.className = 'item-cart row mt-2';
			swatchWrapper.parentNode.insertBefore(itemCartEl, swatchWrapper.nextSibling);
		}
		itemCartEl.classList.remove('hide');
		itemCartEl.removeAttribute('data-variant-item-code');
		itemCartEl.innerHTML =
			'<div class="col-md-12"><div class="mt-2"><span class="no-stock out-of-stock">' +
			__("Out of stock") + '</span></div></div>';
	}

	function getPriceHtml(productInfo) {
		var priceInfo = productInfo.price;
		var html = '<div class="product-price" itemprop="offers" itemscope itemtype="https://schema.org/AggregateOffer">';
		var mainPrice = priceInfo.formatted_price_sales_uom || priceInfo.formatted_price || '';
		if (priceInfo.formatted_mrp) {
			html += '<span itemprop="lowPrice">' + mainPrice + '</span>';
			html += '<small class="formatted-price"><s>MRP ' + priceInfo.formatted_mrp + '</s></small>';
			var discount = priceInfo.formatted_discount_percent || priceInfo.formatted_discount_rate;
			if (discount) {
				html += '<small class="ml-1 formatted-price in-green">-' + discount + '</small>';
			}
		} else {
			html += '<span itemprop="highPrice">' + mainPrice + '</span>';
		}
		if (priceInfo.formatted_price && productInfo.uom) {
			html += '<small class="formatted-price ml-2">(' + priceInfo.formatted_price + ' / ' + productInfo.uom + ')</small>';
		}
		html += '</div>';
		return html;
	}

	function getStockHtml(productInfo) {
		if (productInfo.on_backorder) {
			return '<div class="mt-2"><span class="no-stock out-of-stock" style="color: var(--primary-color);">' +
				__("Available on backorder") + '</span></div>';
		}
		if (productInfo.in_stock === 0) {
			return '<div class="mt-2"><span class="no-stock out-of-stock">' + __("Out of stock") + '</span></div>';
		}
		if (productInfo.in_stock === 1) {
			var qtyHtml = '';
			if (productInfo.show_stock_qty && productInfo.stock_qty) {
				qtyHtml = ' (' + productInfo.stock_qty + ')';
			}
			return '<div class="mt-2"><span class="in-green has-stock">' + __("In stock") + qtyHtml + '</span></div>';
		}
		return '';
	}

	function getCartActionsHtml(itemCode, productInfo, cartSettings) {
		var canShowCartActions = productInfo.price && (cartSettings.allow_items_not_in_stock || productInfo.in_stock);
		if (!canShowCartActions) return '';

		return (
			'<div class="mt-6 mb-5"><div class="mb-4 d-flex">' +
				'<a href="/cart" class="btn btn-light btn-view-in-cart hidden mr-2 font-md" role="button">' +
					(cartSettings.enable_checkout ? __("View in Cart") : __("View in Quote")) +
				'</a>' +
				'<button data-item-code="' + frappe.utils.escape_html(itemCode) + '" class="btn btn-primary btn-add-to-cart mr-2 w-30-40">' +
					'<span class="mr-2">' + frappe.utils.icon('assets', 'md') + '</span>' +
					(cartSettings.enable_checkout ? __("Add to Cart") : __("Add to Quote")) +
				'</button>' +
			'</div></div>'
		);
	}

	// An app's own action for this date (rfhq_item_actions) stands where Add to Cart would: the label and the path are the app's, checked on the server.
	function getItemActionHtml(action) {
		var href = String(action.href || '');
		if (href.charAt(0) !== '/' || href.indexOf('//') === 0) return '';
		return '<div class="mt-6 mb-5"><div class="mb-4 d-flex">' +
			'<a class="btn btn-primary rfhq-item-action mr-2" href="' + frappe.utils.escape_html(href) + '">' + frappe.utils.escape_html(action.label || '') + '</a>' +
			'</div></div>';
	}

	function renderItemCart(itemCode, productInfo, cartSettings, skipResync, itemAction) {
		if (!cartSettings.enabled) return;

		if (!itemCartEl) {
			itemCartEl = document.createElement('div');
			itemCartEl.className = 'item-cart row mt-2';
			swatchWrapper.parentNode.insertBefore(itemCartEl, swatchWrapper.nextSibling);
		}

		itemCartEl.classList.remove('hide');
		itemCartEl.setAttribute('data-variant-item-code', itemCode);

		var html = '<div class="col-md-12">';
		if (cartSettings.show_price && productInfo.price) {
			html += getPriceHtml(productInfo);
		} else if (productInfo.uom) {
			html += __("UOM") + ' : ' + productInfo.uom;
		}
		if (cartSettings.show_stock_availability) {
			html += getStockHtml(productInfo);
		}
		html += itemAction && itemAction.href
			? getItemActionHtml(itemAction)
			: getCartActionsHtml(itemCode, productInfo, cartSettings);
		html += '</div>';

		itemCartEl.innerHTML = html;

		// Let pdp_quantity.js (which reads .item-cart[data-variant-item-code]
		// live) pick up the qty spinner / button visibility for this item.
		// skipResync: a cart-preselection re-render already knows the real
		// cart state - triggering this here would just re-fetch the same
		// selection data in a loop (see refreshCartSelection's debounce note).
		if (!skipResync && window.jQuery && typeof window.jQuery === 'function') {
			window.jQuery(document).trigger('cart-updated');
		}
	}

	// No global handler exists for PDP .btn-add-to-cart outside of the
	// non-variant item_add_to_cart.html's own inline script (and that markup
	// isn't rendered on variant templates at all) - so bind our own here,
	// scoped to the dynamically-created .item-cart block.
	document.addEventListener('click', function (e) {
		var btn = e.target.closest('.item-cart[data-variant-item-code] .btn-add-to-cart');
		if (!btn || !itemCartEl || !itemCartEl.contains(btn) || btn.disabled) return;

		btn.disabled = true;
		var itemCode = btn.getAttribute('data-item-code');
		webshop.webshop.shopping_cart.update_cart({
			item_code: itemCode,
			qty: 1,
			callback: function (r) {
				btn.disabled = false;
				if (r && !r.exc && window.jQuery && typeof window.jQuery === 'function') {
					window.jQuery(document).trigger('cart-updated');
				}
			}
		});
	});

	swatchWrapper.addEventListener('click', function (e) {
		var pill = e.target.closest('.variant-pill');
		if (!pill || pill.classList.contains('variant-pill-disabled') || pill.classList.contains('variant-pill-oos')) return;

		var row = pill.closest('.variant-attr-row');
		if (!row) return;

		// The shopper is changing context - any out-of-stock pills discovered
		// under the *previous* combination aren't necessarily still out of
		// stock under this one, so let them be rediscovered.
		clearOosPills();
		delete swatchContainer._stockSearch;

		Array.prototype.forEach.call(row.querySelectorAll('.variant-pill'), function (p) {
			p.classList.toggle('active', p === pill);
		});
		swatchContainer.dataset.selectionSource = 'user';

		resolveVariant(undefined, row.getAttribute('data-attribute'));
	});

	loadVariantOptions();

	// Keep pre-selection in sync with the cart: fetch it once up front, and
	// again whenever the cart changes (including from our own resolved
	// Add to Cart clicks, via the same 'cart-updated' event pdp_quantity.js
	// already triggers/listens for).
	refreshCartSelection();
	if (window.jQuery && typeof window.jQuery === 'function') {
		window.jQuery(document).on('cart-updated', refreshCartSelection);
	}
});
